import {
  ButtonContainer,
  ButtonsBloc,
  FGCheckboxInput,
  FGListen,
  FGTextAreaInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  DogOwnershipChangeRequestApi,
  EDogType,
  ERequestState,
  PedigreeDuplicataRequestApi,
  PedigreeDuplicataRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
} from "../../../../../api";
import {
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
  FGSRSHDateMaskInput,
  UploadSharepointButton,
} from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { RequestDogOwnerList } from "../../../RequestDogOwnerList";

export interface IPedigreeDuplicataRequestDetailsProps {
  pedigreeDuplicataRequestId: number;
  editMode: boolean;
  parentRequestId: number;
}

export const PedigreeDuplicataRequestDetails: React.FunctionComponent<IPedigreeDuplicataRequestDetailsProps> = ({
  pedigreeDuplicataRequestId,
  editMode,
  parentRequestId,
}) => {
  const isChild = React.useMemo(() => parentRequestId > 0, [parentRequestId]);
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(PedigreeDuplicataRequestApi);
  const rshApi = useApiService(DogOwnershipChangeRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const { showDialogPromise } = useDialog();
  const { openNewTab } = useWindow();
  const [reprintLoading, setreprintLoading] = React.useState(false);
  const [dogIdValue, setDogIdValue] = React.useState(0);
  const [dogIdRequest, setDogIdRequest] = React.useState(true);

  const { data, loading, saving, saveItem, refresh } = useCrudApi<PedigreeDuplicataRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: async (id = pedigreeDuplicataRequestId) => {
          if (id > 0) {
            return api.getPedigreeDuplicataRequest(id);
          }
          if (isChild) {
            var dog = await rshApi.getDogOwnershipChangeRequestDogId(parentRequestId);
            return new PedigreeDuplicataRequestDto({
              dogId: dog,
              request: new RequestDto({
                requestDate: new Date(),
                status: ERequestState.REQUESTED,
              }),
            }).toJSON();
          }
          return new PedigreeDuplicataRequestDto({
            request: new RequestDto({
              requestDate: new Date(),
              status: ERequestState.REQUESTED,
            }),
          }).toJSON();
        },
        saveApiFn: (d: PedigreeDuplicataRequestDto) => api.savePedigreeDuplicataRequest(d),
        onSavedRoute: (d) => `${ERoutes.pedigreeDuplicata}/${d.pedigreeDuplicataRequestId}/detail/edit`,
      }),
      [api, isChild, parentRequestId, pedigreeDuplicataRequestId, rshApi]
    ),
    [pedigreeDuplicataRequestId]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (pedigreeDuplicataRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [pedigreeDuplicataRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  React.useEffect(() => {
    if (data && dogIdRequest) {
      setDogIdValue(data.dogId);
      setDogIdRequest(false);
    }
  }, [data, dogIdRequest]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-authorized", t(ETLCodes.DogIsNotBelgianWithPedigree), async function (value) {
          const type = await dogApi.getDogType(value);
          return type === EDogType.BelgianDogWithPedigree;
        })
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, pedigreeDuplicataRequestId);
          return res.value;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (pedigreeDuplicataRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (pedigreeDuplicataRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (pedigreeDuplicataRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            if (isChild) {
              return true;
            }
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          })
          .test("is-owner-or-breeder", t(ETLCodes.IsNotOwnerOrBreeder), async function (value) {
            if (isChild) {
              return true;
            }
            const res = await dogApi.isDogOwnerOrBreeder(dogIdValue, value);
            return res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data?.request?.status, dogApi, dogIdValue, isChild, pedigreeDuplicataRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  React.useEffect(() => {
    if (dogIdValue > 0) {
    }
  }, [dogIdValue]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  const reprintRequest = React.useCallback(async () => {
    setreprintLoading(true);
    try {
      await api.pedigreeDuplicataReprint(pedigreeDuplicataRequestId);
    } catch {
      showError(t(ETLCodes.Error));
    }
    setreprintLoading(false);
  }, [api, pedigreeDuplicataRequestId, t]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        saving={saving || saveChecking}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.pedigreeDuplicata)}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen
          field="dogId"
          onChanged={(value) => {
            setDogIdValue(value);
          }}
        />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: pedigreeDuplicataRequestId > 0 && (
              <ButtonContainer>
                <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                {data?.request?.status === ERequestState.EXECUTED ? (
                  <UploadSharepointButton
                    text={t(ETLCodes.RePrint)}
                    loading={reprintLoading}
                    onClick={() => reprintRequest()}
                  />
                ) : null}
              </ButtonContainer>
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                pedigreeDuplicataRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
              info={isChild ? null : <InformationTooltip tooltipMessage={ETLCodes.MustBeOwnerOrBreeder} />}
              dogId={isChild ? null : dogIdValue}
              filterBreeder={true}
              showWarning={true}
            />
            <FGCheckboxInput name="isRenewal" label={t(ETLCodes.Renewal)} />
          </div>
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
            growVertically
            rows={6}
          />
        </FieldGroup>

        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.pedigreeDuplicata}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
        <RequestDogOwnerList dogId={dogIdValue} showTemporary={false} />
      </SmallFormGenerator>
    </>
  );
};
